'use client';
import {
  BenzingaLogo,
  BenzingaEsLogo,
  BenzingaFrLogo,
  BenzingaItLogo,
  BenzingaCryptoLogo,
  BenzingaKoreaLogo,
  BenzingaJapanLogo,
  LogoVariant,
} from '@benzinga/logos-ui';
import { appEnvironment, appName } from '@benzinga/utils';
import { usePathname } from 'next/navigation';
import React from 'react';
import styles from './NavigationLogo.module.scss';
import classNames from 'classnames';

const LEARN_LOGO = 'https://cdnwp-s3.benzinga.com/wp-content/uploads/2026/09/14180336/learn-logo.png';

export interface NavigationLogoProps {
  logoVariant?: LogoVariant;
  deviceType?: 'mobile' | 'desktop';
}

const LogosVariants = {
  france: {
    desktop: {
      Component: BenzingaFrLogo,
      props: {},
    },
    mobile: {
      Component: BenzingaFrLogo,
      props: {
        variant: 'light',
      },
    },
  },
  italy: {
    desktop: {
      Component: BenzingaItLogo,
      props: {},
    },
    mobile: {
      Component: BenzingaItLogo,
      props: {
        variant: 'light',
      },
    },
  },
  japan: {
    desktop: {
      Component: BenzingaJapanLogo,
      props: {},
    },
  },
  korea: {
    desktop: {
      Component: BenzingaKoreaLogo,
      props: {},
    },
  },
  spain: {
    desktop: {
      Component: BenzingaEsLogo,
      props: {},
    },
    mobile: {
      Component: BenzingaEsLogo,
      props: {
        variant: 'light',
      },
    },
  },
};

export const NavigationLogo: React.FC<NavigationLogoProps> = ({ deviceType = 'desktop', logoVariant = '' }) => {
  const isMoneyApp = appEnvironment().isApp(appName.money);
  const pathname = usePathname();
  const isLearn = isMoneyApp && !!pathname?.startsWith('/learn');
  const siteLabel = isLearn ? { href: '/learn', text: 'Learn' } : { href: '/money', text: appEnvironment().app() };

  const LogoVariantData = React.useMemo(
    () => LogosVariants[logoVariant]?.[deviceType] ?? LogosVariants[logoVariant]?.['desktop'] ?? null,
    [logoVariant, deviceType],
  );

  return (
    <div className={classNames(styles.logoWrapper, 'logo-wrapper mobile-logo-wrapper')}>
      <span className="sr-only">Benzinga</span>
      {deviceType === 'mobile' ? (
        LogoVariantData ? (
          <a aria-label={LogoVariantData.label ?? 'Click to go home'} href="/">
            <LogoVariantData.Component {...LogoVariantData.props} />
          </a>
        ) : (
          <>
            <a aria-label="Click to go home" href="/">
              <BenzingaLogo deviceType={deviceType} variant="light" />
            </a>
            {isLearn && (
              <a aria-label="Click to go to Benzinga Learn" className={styles.learnLabelLink} href="/learn">
                <img alt="Learn" className={styles.learnLabel} src={LEARN_LOGO} />
              </a>
            )}
          </>
        )
      ) : logoVariant === 'crypto' && appEnvironment().isApp(appName.bz) ? (
        <a aria-label="Click to go to the Crypto home page" className="benzinga-crypto-logo-wrapper" href="/crypto">
          <BenzingaCryptoLogo />
        </a>
      ) : (
        <>
          {LogoVariantData ? (
            <a aria-label={LogoVariantData.label ?? 'Click to go home'} href="/">
              <LogoVariantData.Component {...LogoVariantData.props} />
            </a>
          ) : (
            <a aria-label="Click to go home" href="/">
              <BenzingaLogo deviceType={deviceType} variant={isMoneyApp ? 'light' : 'dark'} />
            </a>
          )}
          {isMoneyApp &&
            (isLearn ? (
              <a aria-label="Click to go to Benzinga Learn" className={styles.learnLabelLink} href="/learn">
                <img alt="Learn" className={styles.learnLabel} src={LEARN_LOGO} />
              </a>
            ) : (
              <a aria-label={`Click to go to Benzinga ${siteLabel.text}`} href={siteLabel.href}>
                <div className={styles.siteLabel}>
                  <span>{siteLabel.text}</span>
                </div>
              </a>
            ))}
        </>
      )}
    </div>
  );
};
